import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { MetaLayerDefinition, SearchResultFlat, useLayers } from '@agissc/ktag-map';
import { SearchBar } from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';
import useToggleTopicLayer from '../../hooks/useToggleTopicLayer';
import useAppState from '../../../AppStore';
import { getFavouritLayers, getFilteredTopics } from './filterTopicLayers';
import { MapTypeCategory } from '../api/Contracts';
import { useDebouncedState } from '../../../utils/useDebouncedState';
import useDisplaySize from '../../../useDisplaySize';
import { ToolNames } from '../../ToolNames';
import SearchLayer from '../../../components/searchresultitem/SearchLayer';
import MultiSelectChips from '../../../components/multiselectchips/MultiSelectChips';
import VirtualizedList from '../../../components/list/VirtualizedList';
import { useFavorites } from '../../hooks/useFavorites';
import { useTracking } from '../../../utils/tracking/TrackingContext';
import SearchResultItemPlaceholder from '../../../components/searchresultitem/SearchResultItemPlaceholder';

const categoryOptions = [...MapTypeCategory].map(([key, value]) => ({
  key,
  value,
  tooltip: `${value}_tooltip`,
}));

const AvailableTopicLayers = () => {
  const searchRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation('agisviewer');
  const { isSmallScreen } = useDisplaySize();
  const { addTopicLayer, removeTopicLayer } = useToggleTopicLayer();
  const { Tracking } = useTracking();

  const selectedTopicLayers = useAppState((state) => state.selectedTopicLayers);
  const setActiveView = useAppState((state) => state.setActiveView);
  const setActiveTool = useAppState((state) => state.setActiveTool);
  const setNewAddedTopicLayer = useAppState((state) => state.setNewAddedTopicLayer);
  const setPreviewLayer = useAppState((state) => state.setPreviewLayer);
  const topicCategoryFilter = useAppState((state) => state.topicLayerCategoryFilter);
  const toggleTopicLayerFilter = useAppState((state) => state.toggleTopicLayerFilter);

  const { getUserFavorites, toggleUserFavorite } = useFavorites();
  const favorites = getUserFavorites();

  const [, setPreviewLayerDebounce] = useDebouncedState(
    useAppState((state) => state.previewLayer),
    useAppState((state) => state.setPreviewLayer),
    300,
  );

  const [topicLayerFilter, setTopicLayerFilter] = useDebouncedState(
    useAppState((state) => state.topicLayerFilter),
    useAppState((state) => state.setTopicLayerFilter),
    300,
  );

  useEffect(() => {
    searchRef.current?.focus();
    return () => {
      setPreviewLayer(undefined);
    };
  }, [topicLayerFilter]);

  const onClearHandler = () => {
    Tracking.Topics.clearSearchbox();
  };

  useEffect(() => {
    const element = searchRef.current?.parentElement?.querySelectorAll(
      '.searchbar__clear-button',
    )[0] as HTMLButtonElement;

    element?.addEventListener('click', onClearHandler);

    return () => {
      element?.removeEventListener('click', onClearHandler);
    };
  }, [searchRef]);

  const isFavorite = (id: any) => id in favorites;
  const handleToggleFavorite = (
    topicLayer: SearchResultFlat | MetaLayerDefinition,
    trackerTrigger: string,
  ) => {
    if (isFavorite(topicLayer.id)) {
      Tracking.Favorites.removeFavorite(topicLayer.id, 'Topic', trackerTrigger);
    } else {
      Tracking.Favorites.addFavorite(topicLayer.id, 'Topic', trackerTrigger);
    }
    toggleUserFavorite(topicLayer);
  };

  const onClickTopicLayer = useCallback(
    (
      topicLayer: MetaLayerDefinition,
      isSelected: boolean,
      trackerTrigger: string,
      closeView: boolean = false,
    ) => {
      setNewAddedTopicLayer(topicLayer.id);

      if (isSelected) {
        Tracking.Topics.removeTopic(
          topicLayer.id,
          selectedTopicLayers.length - 1,
          'topics',
          trackerTrigger,
        );
        removeTopicLayer(topicLayer);
      } else {
        Tracking.Topics.addTopic(
          topicLayer.id,
          selectedTopicLayers.length + 1,
          'topics',
          trackerTrigger,
        );
        addTopicLayer(topicLayer);
      }

      if (isSmallScreen) {
        setActiveTool(ToolNames.identify);
      } else if (closeView) {
        setActiveView('mapContent_tool_main');
      }
    },
    [
      addTopicLayer,
      removeTopicLayer,
      setActiveTool,
      setActiveView,
      setNewAddedTopicLayer,
      isSmallScreen,
    ],
  );

  const handleclickMultiSelectChips = (categorie: string) => {
    const newArray = [...topicCategoryFilter];
    if (newArray.includes(categorie)) {
      newArray.splice(newArray.indexOf(categorie), 1);
    } else {
      newArray.push(categorie);
    }
    Tracking.Topics.changeTopicCategoryFilter(newArray.toString());
    toggleTopicLayerFilter(categorie);
  };

  const showFavorites = useMemo(
    () =>
      !topicLayerFilter &&
      Object.values(favorites).filter((value) => 'metadataUrl' in value).length > 0,
    [topicLayerFilter, favorites],
  );

  const { data, isLoading } = useLayers();

  const filteredLayers = useMemo(() => {
    const layers = showFavorites
      ? getFavouritLayers(data, Object.keys(favorites))
      : getFilteredTopics(data, topicLayerFilter, topicCategoryFilter);

    return layers;
  }, [data, favorites, showFavorites, topicLayerFilter, topicCategoryFilter]);

  const estimateSize = isSmallScreen ? 63 : 68;

  return (
    <div className="tooldialog__wrapper">
      <div
        className={classNames('available-topic-layers__search', {
          'available-topic-layers__search-border': !showFavorites,
        })}
      >
        {!isSmallScreen && (
          <SearchBar
            placeholder={t('layer_selection_searchbar_placeholder')}
            search={topicLayerFilter}
            setSearch={setTopicLayerFilter}
            ref={searchRef}
            disabled={isLoading}
            small
          />
        )}

        <div className="available-topic-layers__filter">
          <MultiSelectChips
            options={categoryOptions}
            selectedOptions={topicCategoryFilter}
            disabled={isLoading}
            onClick={handleclickMultiSelectChips}
          />
        </div>
      </div>

      {showFavorites && <h2 className="favorite-title">{t('favorites')}</h2>}

      {isLoading ? (
        <SearchResultItemPlaceholder />
      ) : (
        <VirtualizedList
          items={filteredLayers}
          estimateSize={() => estimateSize}
          renderItem={(item) => {
            const isSelected = selectedTopicLayers.includes(item.id);

            return (
              <SearchLayer
                layer={item}
                markedText={topicLayerFilter}
                isSelected={isSelected}
                isFavorite={item.id in favorites}
                onClick={() => onClickTopicLayer(item, isSelected, 'listitem', true)}
                onIconClick={() => onClickTopicLayer(item, isSelected, 'plusminusicon')}
                onToggleFavorite={() => handleToggleFavorite(item, 'layer')}
                onMouseEnter={() => setPreviewLayerDebounce(item.id)}
                onMouseLeave={() => setPreviewLayerDebounce(undefined)}
              />
            );
          }}
        />
      )}
    </div>
  );
};

export default AvailableTopicLayers;
